import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProductShowcase from './ProductShowcase';
import { isRealSite, withRealPrefix } from '../utils/siteMode';

const MOBILE_BREAKPOINT = 768;

const REAL_FEATURE_CARDS = [
  {
    icon: '🎨',
    title: '蓝卡模玩创作实验室',
    description: '发挥创意，制作你的专属模玩',
    link: '/studio',
  },
  {
    icon: '🏪',
    title: '工作室产品',
    description: '发现和收藏精彩的模玩作品',
    link: '/products',
  },
  {
    icon: '👥',
    title: '社区交流',
    description: '与模玩爱好者分享心得',
    link: '/forum',
  },
  {
    icon: '📺',
    title: '工作室视频',
    description: '蓝卡模玩 B站投稿视频',
    link: '/studio-videos',
  },
  {
    icon: '📚',
    title: '教程学习',
    description: '从入门到精通的教程指南',
    link: '/tutorials',
  },
  {
    icon: '🎖️',
    title: 'reRA 载具预览',
    description: '红警体素载具：MIX / INI 拼装与 3D 预览',
    link: '/reRA/',
    isExternal: true,
  },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
}

function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, handleQQCallback } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMobile = useIsMobile();
  const isReal = isRealSite(location.pathname);

  function getQueryParam(name) {
    const params = new URLSearchParams(location.search);
    return params.get(name);
  }

  useEffect(() => {
    async function processQQCallback() {
      const code = getQueryParam('code');
      const state = getQueryParam('state');

      if (code && state === 'qq') {
        setLoading(true);
        setError(null);

        const result = await handleQQCallback(code);

        if (result.success) {
          // 登录成功后留在主页，不自动跳转社区
          navigate(withRealPrefix('/Home'), { replace: true });
        } else {
          setError(result.error);
        }

        setLoading(false);
      }
    }

    processQQCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isReal]);

  const featureCards = isReal
    ? REAL_FEATURE_CARDS.map((card) => ({
        ...card,
        link: card.isExternal ? card.link : withRealPrefix(card.link),
      }))
    : [
        {
          icon: '🏪',
          title: '工作室产品',
          description: '浏览工作室模玩产品展示',
          link: '/products',
        },
      ];

  const gridStyle = isReal
    ? (isMobile
        ? { ...styles.featuresGrid, gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }
        : styles.featuresGrid)
    : {
        ...styles.featuresGrid,
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(280px, 400px)',
        justifyContent: 'center',
        maxWidth: '480px',
        margin: '0 auto',
      };

  return (
    <div style={styles.container}>
      <div style={{ opacity: isReal ? 0.4 : 0.5 }}>
        <ProductShowcase />
      </div>

      <div style={isMobile ? { ...styles.contentLayer, padding: '20px 16px' } : styles.contentLayer}>
        <section style={isMobile ? { ...styles.welcomeSection, padding: '24px 16px' } : styles.welcomeSection}>
          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>正在登录，请稍候...</p>
            </div>
          ) : error ? (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>❌ {error}</p>
              {isReal && (
                <button
                  style={styles.retryButton}
                  onClick={() => navigate(withRealPrefix('/login'))}
                >
                  重新登录
                </button>
              )}
            </div>
          ) : (
            <>
              <h1 style={isMobile ? { ...styles.welcomeTitle, fontSize: 26 } : styles.welcomeTitle}>
                {isReal
                  ? (isAuthenticated && user
                      ? `欢迎回来，${user.nickname || '创作者'}！`
                      : '欢迎来到蓝卡模玩社区')
                  : '蓝卡模玩工作室'}
              </h1>
              <p style={isMobile ? { ...styles.welcomeSubtitle, fontSize: 15, marginBottom: 24 } : styles.welcomeSubtitle}>
                {isReal
                  ? (isAuthenticated
                      ? '开始你的模玩创作之旅'
                      : '登录后解锁更多精彩内容')
                  : '专业模玩设计与制作 · 产品展示'}
              </p>

              {isReal && isAuthenticated && user && (
                <div style={isMobile ? { ...styles.userCard, padding: '12px 20px', gap: 12 } : styles.userCard}>
                  <img
                    src={user.avatar || user.figureurl_qq_2 || user.figureurl_2}
                    alt="头像"
                    style={isMobile ? { ...styles.userAvatar, width: 44, height: 44 } : styles.userAvatar}
                  />
                  <div style={styles.userInfo}>
                    <div style={isMobile ? { ...styles.userName, fontSize: 16 } : styles.userName}>{user.nickname || user.name}</div>
                    <div style={isMobile ? { ...styles.userStatus, fontSize: 12 } : styles.userStatus}>🎮 模玩爱好者</div>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <section style={isMobile ? { ...styles.featuresSection, marginTop: 24, padding: '0 16px' } : styles.featuresSection}>
          <h2 style={isMobile ? { ...styles.sectionTitle, fontSize: 20, marginBottom: 16 } : styles.sectionTitle}>
            {isReal ? '探索功能' : '工作室产品'}
          </h2>
          <div style={gridStyle}>
            {featureCards.map((card) => (
              <FeatureCard
                key={card.title}
                icon={card.icon}
                title={card.title}
                description={card.description}
                link={card.link}
                isExternal={card.isExternal}
                navigate={navigate}
                isMobile={isMobile}
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, link, isExternal, navigate, isMobile }) {
  const handleClick = () => {
    if (link) {
      if (isExternal) {
        window.location.href = link;
      } else if (navigate) {
        navigate(link);
      }
    }
  };

  const cardStyle = isMobile
    ? { ...styles.featureCard, padding: 14, borderRadius: 12 }
    : styles.featureCard;

  const iconStyle = isMobile ? { ...styles.featureIcon, fontSize: 28, marginBottom: 8 } : styles.featureIcon;
  const titleStyle = isMobile ? { ...styles.featureTitle, fontSize: 14, marginBottom: 4 } : styles.featureTitle;
  const descStyle = isMobile ? { ...styles.featureDesc, fontSize: 11, lineHeight: 1.4 } : styles.featureDesc;

  return (
    <div
      style={{ ...cardStyle, cursor: link ? 'pointer' : 'default' }}
      onClick={handleClick}
      onMouseEnter={(e) => {
        if (!isMobile) {
          e.currentTarget.style.transform = 'translateY(-8px)';
          e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.2)';
      }}
    >
      <div style={iconStyle}>{icon}</div>
      <h3 style={titleStyle}>{title}</h3>
      <p style={descStyle}>{description}</p>
      {link && <span style={isMobile ? { ...styles.cardArrow, fontSize: 14, marginTop: 6 } : styles.cardArrow}>→</span>}
    </div>
  );
}

const styles = {
  container: {
    minHeight: '90vh',
    background: 'linear-gradient(180deg, #1a1d23 0%, #282c34 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  contentLayer: {
    position: 'relative',
    zIndex: 10,
    padding: '40px 20px',
  },
  welcomeSection: {
    textAlign: 'center',
    padding: '60px 20px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid rgba(18, 183, 245, 0.2)',
    borderTop: '4px solid #12B7F5',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#9ca3af',
    fontSize: '16px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '16px',
  },
  retryButton: {
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  welcomeTitle: {
    fontSize: '42px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '16px',
    letterSpacing: '1px',
    textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
  },
  welcomeSubtitle: {
    fontSize: '18px',
    color: '#9ca3af',
    marginBottom: '40px',
  },
  userCard: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '16px',
    backgroundColor: 'rgba(37, 40, 48, 0.9)',
    padding: '20px 32px',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    WebkitBackdropFilter: 'blur(10px)',
    backdropFilter: 'blur(10px)',
  },
  userAvatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #12B7F5',
  },
  userInfo: {
    textAlign: 'left',
  },
  userName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
  },
  userStatus: {
    fontSize: '14px',
    color: '#9ca3af',
    marginTop: '4px',
  },
  featuresSection: {
    maxWidth: '1200px',
    margin: '60px auto 0',
    padding: '0 20px',
  },
  sectionTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '40px',
    textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '24px',
  },
  featureCard: {
    position: 'relative',
    backgroundColor: 'rgba(37, 40, 48, 0.9)',
    borderRadius: '16px',
    padding: '32px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    textAlign: 'center',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    WebkitBackdropFilter: 'blur(10px)',
    backdropFilter: 'blur(10px)',
  },
  featureIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  featureTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '8px',
  },
  featureDesc: {
    fontSize: '14px',
    color: '#9ca3af',
    lineHeight: '1.6',
  },
  cardArrow: {
    display: 'inline-block',
    marginTop: '12px',
    fontSize: '18px',
    color: '#60a5fa',
    transition: 'transform 0.2s ease',
  },
};

export default Home;
